import { DeleteOutlined, DownOutlined, PlusOutlined } from "@ant-design/icons";
import {
  Button,
  Divider,
  Drawer,
  Dropdown,
  MenuProps,
  Popconfirm,
  Space,
  Table,
  Tag,
  Tooltip,
  message,
} from "antd";
import { Header } from "antd/es/layout/layout";
import PFIBillingDetails from "../../../components/pro_forma_invoice/pro_forma_invoice_details/pfi_billing_details";
import PFIOrderDetails from "../../../components/pro_forma_invoice/pro_forma_invoice_details/pfi_order_details";
import { isZohoActivityStatus } from "../../../constants/gst.constants";

import moment from "moment";
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router";
import { Link } from "react-router-dom";
import { fetchClientDetails } from "../../../apis/client.api";
import { getPurchaseOrdersoptimzed } from "../../../apis/po.api";
import {
  editPI,
  fetchEstimateLogsByLeadId,
  fetchPIfromLeadId,
  fetchPaymentSummary,
  fetchWalletBalance,
  getEstimateTransactions,
  getPFIInvoices,
  removeEstimateCharge,
  removeInvoiceFromPI,
  settleWalletTransactions
} from "../../../apis/pro_forma_invoice.api";
import { fetchUserDetails } from "../../../apis/user.api";
import { AddChargesDrawer } from "../../../components/pro_forma_invoice/add_charges_drawer";
import DeliveryAddressDrawer from "../../../components/pro_forma_invoice/delivery_address_drawer";
import GSTRetrofitSidebar from "../../../components/pro_forma_invoice/gst_retrofit_sidebar";
import { PIEditAddress } from "../../../components/pro_forma_invoice/pi_edit_address";
import ProFormaLogCard from "../../../components/pro_forma_invoice/pro_forma_log_card";
import TaxAmount from "../../../components/pro_forma_invoice/tax_amount";
import { usePermissions } from "../../../custom_hooks/permission/permission_hook";
import { piEditLockReason } from "../../../helpers/estimate_edit_window";
import { openAuthedPdf } from "../../../helpers/pdf_helper";
import { Client } from "../../../interfaces/client.interface";
import styles from "./index.module.scss";

const ESTIMATE_STATUSES_BLOCKING_PI_EDIT = [
  "order_placed",
  "order_confirmed",
  "partly_shipped",
  "shipped",
  "partly_delivered",
  "delivered",
  "payment_pending",
  "refunded"
];

// Charges the "Add Charges" drawer manages — the only ones removable from this page.
const REMOVABLE_CHARGE_TYPES = ["transport charges", "unloading charges", "additional charges"];
// Charges are added here without payment, so they can only be added or removed until the order is
// placed (removal is blocked server-side too).
const CHARGES_LOCKED_STATUSES = [
  "order_placed",
  "order_confirmed",
  "partly_shipped",
  "shipped",
  "partly_delivered",
  "delivered",
  "refunded",
];

const ProFormaInvoiceDetails = () => {
  const { lead_id } = useParams();
  const navigate = useNavigate();
  const { hasPermission } = usePermissions();
  const userRole = localStorage.getItem("user_role");

  const [data, setData] = useState<any>({});
  // The URL now carries the lead id; the active estimate is resolved from it.
  // `estimateId` is the real estimate id used for all estimate-scoped calls.
  const estimateId = data?.id;
  const [logs, setLogs] = useState<Array<any>>([]);
  const [see_logs, setSeeLogs] = useState(false);
  const [activitySidebarOpen, setActivitySidebarOpen] = useState(false);
  // const [branch_data, setBranchData] = useState<any>({});
  const [copy_clipboard_select, setCopyClipboardSelect] = useState(false);
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  const [invoices, setInvoices] = useState<Array<any>>([]);
  const [currentUser, setCurrentUser] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [showEditPIEditAddress, setShowEditPIEditAddress] = useState<
    "shipping" | "billing" | null
  >(null);

  const [delivery_address_drawer_visible, setDeliveryAddressDrawerVisible] =
    useState(false);

  const [client_data, setClientData] = useState<Client | null>(null);
  const [transactions, setTransactions] = useState<any[]>([]);
  const [addChargesDrawerVisible, setAddChargesDrawerVisible] = useState(false);
  
  const [branchMembers, setBranchMembers] = useState<any[]>([]);
  const [selectedBranch, setSelectedBranch] = useState<any>(null);
  const [walletBalance, setWalletBalance] = useState<number>(0);
  const [activePOCount, setActivePOCount] = useState<number>(0);

  // ── Payment Summary — single source of truth from backend ──────────────
  const [paymentSummary, setPaymentSummary] = useState<{
    total_amount: string;
    paid_amount: string;
    wallet_total: string;
    applicable_wallet: string;
    amount_pending: string;
    transactions: any[];
    wallet_transactions: any[];
  } | null>(null);

  const amountPending = Number(paymentSummary?.amount_pending ?? 0);

  const refreshPaymentSummary = () => {
    if (!estimateId) return;
    fetchPaymentSummary(estimateId).then((res) => {
      if (res) setPaymentSummary(res);
    });
  };

  const [applyingWallet, setApplyingWallet] = useState(false);
  const [awaitingAutoSettle, setAwaitingAutoSettle] = useState(false);

  // Auto-settle: fires when a drawer payment causes amount_pending to reach 0
  useEffect(() => {
    if (!awaitingAutoSettle) return;
    if (amountPending === 0) {
      setAwaitingAutoSettle(false);
      handleSettle();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [amountPending, awaitingAutoSettle]);

  // handleSettle — single action for both auto-trigger and Place Order button
  const handleSettle = async () => {
    try {
      setApplyingWallet(true);

      const result = await settleWalletTransactions({
        estimate_id: data.id,
        client_id: client_data?.id || data?.client,
        organisation_branch_id: selectedBranch?.id ?? null,
      });

      if (!result) return;

      if (result.order_placed) {
        message.success("Order placed successfully!");
      } else if (result.failed) {
        message.warning("Some wallet credits could not be applied. Please retry.");
      }

      getPIData();
      refreshPaymentSummary();
    } catch (e) {
      console.error(e);
      message.error("Error settling wallet. Please retry.");
    } finally {
      setApplyingWallet(false);
    }
  };

  const transactionColumns = [
    {
      title: "Transaction ID",
      key: "id",
      dataIndex: "payment_id",
    },
    {
      title: "Payment Method",
      key: "payment_method",
      dataIndex: "payment_method",
    },
    {
      title: "UUID",
      key: "uuid",
      dataIndex: "uuid",
    },
    {
      title: "Payment Amount",
      key: "payment_amount",
      dataIndex: "payment_amount",
      render: (data: any) => (Number(data) / 100).toFixed(2),
    },
    {
      title: "Status",
      key: "status",
      dataIndex: "status",
      render: (data: any) => {
        return (
          <Tag
            color={
              data === "success"
                ? "green"
                : data === "failed"
                ? "red"
                : "orange"
            }
          >
            {data}
          </Tag>
        );
      },
    },
  ];

  const onSelectChange = (newSelectedRowKeys: React.Key[]) => {
    console.log("selectedRowKeys changed: ", newSelectedRowKeys);
    setSelectedRowKeys(newSelectedRowKeys);
  };

  const openCheckoutModal = () => {
    // TOOD:
  };

  const rowSelection = {
    selectedRowKeys,
    onChange: onSelectChange,
  };
  const hasSelected = selectedRowKeys.length > 0;
  const piEditLock = piEditLockReason(hasPermission);
  const isPastOrderStage = ESTIMATE_STATUSES_BLOCKING_PI_EDIT.includes(
    data?.estimate_status
  );
  const hasActivePOsBlockingEdit =
    data?.estimate_status === "payment_pending" && activePOCount > 0;
  const editDisabledReason = isPastOrderStage
    ? "This estimate has already moved past order placement and can no longer be edited."
    : hasActivePOsBlockingEdit
    ? "A PO has been raised against this estimate. Delete all POs raised against it to enable editing."
    : !hasPermission("pi.update")
    ? "You don't have permission to edit this pro forma invoice."
    : piEditLock;
  const isEditDisabled = Boolean(editDisabledReason);

  const items: MenuProps["items"] = [
    {
      key: "1",
      label: (
        <Tooltip title={editDisabledReason} placement="left">
          <div
            onClick={() => {
              if (!isEditDisabled) {
                window.location.href = "/edit-pro-forma-invoice/" + estimateId;
              }
            }}
          >
            Edit Pro Forma Invoice
          </div>
        </Tooltip>
      ),
      disabled: isEditDisabled,
    },
    {
      key: "2",
      label: (
        <div
          onClick={() => {
            openAuthedPdf(
              `${process.env.REACT_APP_API_URL}/estimate-pdf/` +
                estimateId +
                "/?image=true"
            ).catch(() => {});
          }}
        >
          Download pdf
        </div>
      ),
    },
    {
      key: "6",
      label: (
        <div
          onClick={() =>
            openAuthedPdf(
              `${process.env.REACT_APP_API_URL}/estimate-pdf/${estimateId}/?image=true&b2b=true`
            ).catch(() => {})
          }
        >
          Download B2B Invoice
        </div>
      ),
    },
    ...(data?.estimate_status === "payment_pending"
      ? [
          {
            key: "3",
            label: (
              <div
                className={
                  hasPermission("po.create")
                    ? ""
                    : "unclickable grey-color-text"
                }
                onClick={() => {
                  if (!hasPermission("po.create")) return;
                  navigate("/create-po", { state: { data } });
                }}
              >
                Create PO
              </div>
            ),
          },
        ]
      : []),
  ];

  const columns = [
    {
      title: "S.no",
      dataIndex: "index",
      render: (value: any, data: any, index: number) => <>{index + 1}</>,
    },
    {
      title: "BOQ Item",
      key: "boq_item",
      dataIndex: "boq_item",
      width: 200,
      render: (_: unknown, record: any) => {
        if (hasPermission("pi.replace_brand_with_private_label")) {
          return record["private_label_product_name"] ?? record["boq_item"];
        } else {
          return record["boq_item"];
        }
      },
    },
    hasPermission("pi.private_label_brand_name_column")
      ? {
          key: "private_label_product_name",
          title: "Private Label Name",
          dataIndex: "private_label_product_name",
          width: 200,
        }
      : {},
    {
      title: "MRP",
      key: "mrp",
      dataIndex: "mrp",
      width: 100,
    },
    {
      title: "Category",
      key: "category_name",
      dataIndex: "category_name",
      width: 100,
    },
    {
      title: "Quantity",
      key: "quantity",
      dataIndex: "quantity",
      width: 50,
    },
    {
      title: "Unit",
      key: "unit",
      dataIndex: "unit",
      width: 60,
    },
    // {
    //   title: "Brand",
    //   key: "brand",
    //   dataIndex: "brand",
    //   width: 130,
    //   render: (_: unknown, record: any) => {
    //     // if (hasPermission("pi.replace_brand_with_private_label")) {
    //     //   return record["private_label_brand_name"] ?? record["brand"];
    //     // } else {
    //     //   return record["brand"];
    //     // }
    //     return record["brand"];
    //   },
    // },
    hasPermission("pi.private_label_brand_name_column")
      ? {
          key: "private_label_brand_name",
          title: "Private Label Brand Name",
          dataIndex: "private_label_brand_name",
          width: 130,
        }
      : {},
    hasPermission("pi.view_vendor_column")
      ? {
          key: "vendor",
          title: "Vendor",
          render: (data: any) => {
            const vendors = data?.vendor_data ?? [];
            return vendors.map((vendor: any) => vendor?.name).join(", ");
          },
          width: 150,
        }
      : {},
    // {
    //   title: "Landing Price",
    //   key: "unit_selling_price",
    //   width: 100,
    //   render: (data: { [x: string]: string }) =>
    //     parseFloat(data["unit_selling_price"]),
    // }, {
    {
      title: "Selling Price",
      key: "unit_cost_price",
      width: 100,
      render: (data: { [x: string]: string }) =>
        parseFloat(data["unit_cost_price"]),
    },
    // {
    //   title: "Margin",
    //   key: "margin",
    //   width: 100,
    //   render: (data: { margin: number }) =>
    //     Number(data.margin / (1 + data.margin / 100)).toFixed(2),
    // },
    {
      title: "Tax",
      key: "tax",
      render: (data: { tax_value: any }) => {
        var tax = Number(data?.tax_value);
        return (
          <>
            <div>{tax}%</div>
          </>
        );
      },
    },
    {
      title: "Tax Type",
      key: "tax_type",
      render: (data: { tax_type: string }) => (
        <>
          {data?.tax_type === "igst" && "IGST"}
          {data?.tax_type === "cgst_sgst" && "CGST & SGST"}
          {data?.tax_type === "none" && "None"}
        </>
      ),
    },
    {
      title: "Lead time",
      key: "lead_time",
      render: (data: any) => {
        return (
          <div style={{ maxWidth: 60 }}>
            {/* {parseFloat(data["lead_time"]) > -1 ? (
              <>{data["lead_time"]} days</>
            ) : (
              <></>
            )} */}
            {data["lead_time"] && data["lead_time"] !== null && (
              <>{data["lead_time"]} days</>
            )}
          </div>
        );
      },
    },
    {
      title: "Remarks",
      key: "remarks",
      render: (data: any) => {
        return <div style={{ maxWidth: 200 }}> {data["remarks"]}</div>;
      },
    },
    {
      title: "Amount",
      key: "amount",
      render: (data: { [x: string]: number }) => {
        return data["total_price"];
      },
    },
  ];

  const refreshTransactions = () => {
    if (!estimateId) return;
    getEstimateTransactions(Number(estimateId))
      .then((response) => setTransactions(response))
      .catch((error) => console.error(error));
  };

  const canRemoveCharge = (charge: any) =>
    hasPermission("expressunloadingtag.addel") &&
    !CHARGES_LOCKED_STATUSES.includes(data?.estimate_status) &&
    !piEditLock &&
    REMOVABLE_CHARGE_TYPES.includes(
      String(charge?.charge_type ?? "").trim().toLowerCase()
    );

  // Returned to Popconfirm so its OK button shows the loading state. The refresh is deferred until
  // the popconfirm has finished closing — unmounting the row it is anchored to mid-animation trips
  // antd's ResizeObserver ("loop completed with undelivered notifications").
  const onRemoveCharge = async (chargeId: number) => {
    const result = await removeEstimateCharge({
      estimate_id: data?.id,
      charge_id: chargeId,
    });
    if (result) {
      setTimeout(() => {
        getPIData();
        refreshPaymentSummary();
      }, 300);
    }
  };

  const getPIData = () => {
    fetchPIfromLeadId(lead_id ?? "").then((res) => {
      setData({ ...res, po_items: res?.estimate_items });
      fetchEstimateLogsByLeadId(res?.lead_id).then((res) => {
        setLogs(res);
      });
      if (!res?.payment_link) {
        message.error({
          content:
            "Payment link not generated for this PI. Please revise the PI.",
          duration: 15,
          key: "payment_link_error",
        });
      }
    });
  };

  const refreshActivePOCount = () => {
    if (!estimateId) return;
    getPurchaseOrdersoptimzed({ estimate: estimateId, limit: 100 }).then((res) => {
      const activePOs = (res?.results ?? res ?? []).filter(
        (po: any) => !po?.is_deleted && !po?.is_revised
      );
      setActivePOCount(activePOs.length);
    });
  };

  // Estimate-scoped data depends on the resolved estimate id, which is only
  // known after getPIData() sets `data`. Fire these once we have it.
  useEffect(() => {
    if (!estimateId) return;
    refreshTransactions();
    refreshPaymentSummary();
    fetchInvoicesOfPI();
    refreshActivePOCount();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [estimateId]);

  useEffect(() => {
    getPIData();
    // fetchBranches().then((res) => {
    //   setBranchData(res);
    // });
    // fetchEstimateLogs(pi_id ?? "").then((res) => {
    //   setLogs(res);
    // });

    fetchUserDetails().then((res) => {
      setCurrentUser(res);
    });
  }, []);

  const fetchBasicDetails = (client_id: string) => {
    fetchClientDetails(client_id).then((res) => {
      setClientData(res);
    });
  };

  useEffect(() => {
    if (data?.client) {
      fetchBasicDetails(data?.client);
    }
  }, [data]);

  const refreshWalletBalance = () => {
    const clientId = client_data?.id || data?.client;
    if (clientId) {
      fetchWalletBalance(clientId).then((res) => {
        if (res?.total_balance) {
          setWalletBalance(Number(res.total_balance) / 100);
        } else {
          setWalletBalance(0);
        }
      }).catch(() => setWalletBalance(0));
    }
  };

  useEffect(() => {
    refreshWalletBalance();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [client_data, data]);

  const fetchInvoicesOfPI = () => {
    setLoading(true);
    getPFIInvoices(estimateId).then((res) => {
      setInvoices(res);
      setLoading(false);
    });
  };

  const confirm = async (invoice_id: any) => {
    if (!loading) {
      setLoading(true);
      await removeInvoiceFromPI(invoice_id);
      setLoading(false);
    }
  };

  const cancel = () => {};
  const onDeliveryDetailsFinish = (values: any) => {
    setDeliveryAddressDrawerVisible(false);
    editPI(data?.id, {
      ...values,
      order_placed_time: moment(new Date()).add(5.5, "hours").toISOString(),
      estimate_status: "order_placed",
    }).then((res) => {
      navigate("/order-details/" + estimateId);
    });
  };

  return (
    <section className={`lighter-grey-color-bg`}>
      <Header
        style={{
          position: "sticky",
          top: 0,
          zIndex: 1,
          width: "100%",
        }}
      >
        <div className="d-flex justify-content-between">
          <div className="d-flex gap-4 align-items-center">
            <div className="primary-color-text th-font-size-20 th-font-weight-700">
              Pro Forma Details
            </div>
            <Link
              className="text-decoration-none"
              to={"/client/" + client_data?.id}
              target="_blank"
            >
              <div className=" th-font-weight-600 clover-green-color-text">
                Client :
                <span className=" th-font-weight-400">
                  {" "}
                  {client_data?.f_name}
                </span>
              </div>
            </Link>
            {/* <div className=" th-font-weight-600">
              Quotation No. :
              <span className=" th-font-weight-400"> {data?.estimate_no}</span>
            </div> */}
            <div className=" th-font-weight-600">
              Lead ID :
              <span className=" th-font-weight-400"> {data?.lead_id}</span>
            </div>
          </div>

          <div className="d-flex align-items-center gap-3">
            {hasPermission("pi.update") && ["quote_approval_pending", "payment_pending"].includes(
              data?.estimate_status
            ) &&
              false && (
                <Button
                  className="fw-bold"
                  type="primary"
                  onClick={openCheckoutModal}
                >
                  Checkout
                </Button>
              )}
            <Dropdown menu={{ items }} placement="bottomRight">
              <Button
                type="primary"
                className="th-font-weight-700 d-flex align-items-center"
              >
                Actions
                <DownOutlined />
              </Button>
            </Dropdown>
          </div>
        </div>
      </Header>
      <div className={styles.table_content}>
        <div className="m-4 p-4 px-5 white-color-bg">
          <div className="d-flex justify-content-between">
            <img
              alt="logo"
              src="/main_logo.svg"
              style={{ height: "80px" }}
              className="mt-2"
            />
            <div className="d-flex align-items-center">
              <Button onClick={() => setSeeLogs(true)}>See logs</Button>
            </div>
          </div>
          <div className="row my-4">
            <div className="col-xl-6 col-12">
              <PFIBillingDetails
                name={data?.gst?.label}
                address={data?.gst?.address}
                gst_no={data?.gst?.gst_no}
              />
            </div>
            <div className="col-xl-6 col-12">
              <PFIOrderDetails
                created_by={data?.created_by}
                assigned_to={data?.assigned_to?.f_name}
                client={client_data?.f_name ?? ""}
                lead_id={data?.lead_id}
                estimate_data={data}
                quotation_no={data?.estimate_no}
                status={data?.estimate_status}
                stage={data?.stage}
                hasActivePOs={activePOCount > 0}
                // bank_details={{
                //   bank_account_number: branch_data?.bank_account_number,
                //   bank_ifsc: branch_data?.bank_ifsc,
                //   branch_upi: branch_data?.branch_upi,
                // }}
                setDeliveryAddressDrawerVisible={
                  setDeliveryAddressDrawerVisible
                }
                getPIData={getPIData}
                onWalletTopUp={refreshPaymentSummary}
                amountToBePaid={amountPending}
                onPaymentMade={() => {
                  refreshPaymentSummary();
                  setAwaitingAutoSettle(true);
                }}
              />
            </div>
          </div>

          <Divider />
          <div className="row my-4">
            {data?.bill_address && (
              <div className="col-xl-6 col-12">
                <div className=" p-3 py-2 row mb-2">
                  <div className="d-flex justify-content-between align-items-center">
                    <div className="th-font-size-16 th-font-weight-600 pb-3 primary-color-text">
                      Bill To
                    </div>
                    <Space className="">
                      <Tooltip title={piEditLock}>
                        <Button
                          type="primary"
                          size="small"
                          disabled={Boolean(piEditLock)}
                          onClick={() => {
                            setShowEditPIEditAddress("billing");
                          }}
                        >
                          Edit address
                        </Button>
                      </Tooltip>
                    </Space>
                  </div>

                  <div className="row">
                    <div className="col-4 th-font-weight-500">Name </div>
                    <div className="col-7">
                      <div className="d-flex">
                        <div className="pe-1">:</div>
                        <div>{data?.bill_address?.name}</div>
                      </div>
                    </div>
                  </div>

                  <div className="row">
                    <div className="col-4 th-font-weight-500">Address </div>
                    <div className="col-7">
                      <div className="d-flex">
                        <div className="pe-1">:</div>
                        <div>{data?.bill_address?.address}</div>
                      </div>
                    </div>
                  </div>

                  <div className="row">
                    <div className="col-4 th-font-weight-500">Pincode </div>
                    <div className="col-7">
                      <div className="d-flex">
                        <div className="pe-1">:</div>
                        <div>{data?.bill_address?.pincode}</div>
                      </div>
                    </div>
                  </div>

                  <div className="row">
                    <div className="col-4 th-font-weight-500">
                      Contact person{" "}
                    </div>
                    <div className="col-7">
                      <div className="d-flex">
                        <div className="pe-1">:</div>
                        <div>{data?.bill_address?.contact_person_name}</div>
                      </div>
                    </div>
                  </div>
                  <div className="row">
                    <div className="col-4 th-font-weight-500">
                      Contact number
                    </div>
                    <div className="col-7">
                      <div className="d-flex">
                        <div className="pe-1">:</div>
                        <div>{data?.bill_address?.contact_person_contact}</div>
                      </div>
                    </div>
                  </div>
                    <div className="mt-3 row">
                      <div className="col-4">GST No.</div>
                      <div className="col-8 d-flex">
                        <div>:</div>
                        <div className="w-100 px-2">{data?.client_gst}</div>
                      </div>
                    </div>

                  {isZohoActivityStatus(data?.estimate_status) && (
                    <div className="mt-3 d-flex justify-content-end">
                      <Button size="small" onClick={() => setActivitySidebarOpen(true)}>
                        View Zoho activity
                      </Button>
                    </div>
                  )}
                </div>
              </div>
            )}
            {data?.ship_address && (
              <div className="col-xl-6 col-12">
                <div className=" p-3 py-2 row mb-2">
                  <div className="d-flex justify-content-between align-items-center">
                    <div className="th-font-size-16 th-font-weight-600 pb-3 primary-color-text">
                      Ship To
                    </div>
                    <Space className="">
                      <Tooltip title={piEditLock}>
                        <Button
                          type="primary"
                          size="small"
                          disabled={Boolean(piEditLock)}
                          onClick={() => {
                            setShowEditPIEditAddress("shipping");
                          }}
                        >
                          Edit address
                        </Button>
                      </Tooltip>
                    </Space>
                  </div>
                  <div className="row">
                    <div className="col-4 th-font-weight-500">
                      Address Label{" "}
                    </div>
                    <div className="col-7">
                      <div className="d-flex">
                        <div className="pe-1">:</div>
                        <div>{data?.ship_address?.name}</div>
                      </div>
                    </div>
                  </div>

                  {data?.ship_address?.flat_no && (
                    <div className="row">
                      <div className="col-4 th-font-weight-500">Flat No.</div>
                      <div className="col-7">
                        <div className="d-flex">
                          <div className="pe-1">:</div>
                          <div>{data?.ship_address?.flat_no}</div>
                        </div>
                      </div>
                    </div>
                  )}

                  <div className="row">
                    <div className="col-4 th-font-weight-500">Address </div>
                    <div className="col-7">
                      <div className="d-flex">
                        <div className="pe-1">:</div>
                        <div>{data?.ship_address?.address}</div>
                      </div>
                    </div>
                  </div>

                  <div className="row">
                    <div className="col-4 th-font-weight-500">Pincode </div>
                    <div className="col-7">
                      <div className="d-flex">
                        <div className="pe-1">:</div>
                        <div>{data?.ship_address?.pincode}</div>
                      </div>
                    </div>
                  </div>

                  <div className="row">
                    <div className="col-4 th-font-weight-500">
                      Contact person{" "}
                    </div>
                    <div className="col-7">
                      <div className="d-flex">
                        <div className="pe-1">:</div>
                        <div>{data?.ship_address?.contact_person_name}</div>
                      </div>
                    </div>
                  </div>
                  <div className="row">
                    <div className="col-4 th-font-weight-500">
                      Contact number
                    </div>
                    <div className="col-7">
                      <div className="d-flex">
                        <div className="pe-1">:</div>
                        <div>{data?.ship_address?.contact_person_contact}</div>
                      </div>
                    </div>
                  </div>

                  <div className="row">
                    <div className="col-4 th-font-weight-500">
                      Selected address
                    </div>
                    <div className="col-7">
                      <div className="d-flex">
                        <div className="pe-1">:</div>
                        <div>
                          {data?.ship_address?.address},{" "}
                          {data?.ship_address?.city}{" "}
                          {data?.ship_address?.pincode}
                        </div>
                      </div>
                    </div>
                  </div>
                  {data?.ship_address?.google_map_address && (
                    <div className="row">
                      <div className="col-4 th-font-weight-500">
                        Google Map Address
                      </div>
                      <div className="col-7">
                        <div className="d-flex">
                          <div className="pe-1">:</div>
                          <div>
                            <a
                              href={`https://maps.google.com/?q=${data?.ship_address?.lat},${data?.ship_address?.long}`}
                              target="_blank"
                              rel="noreferrer"
                            >
                              {`https://maps.google.com/?q=${data?.ship_address?.lat},${data?.ship_address?.long}`}
                            </a>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  {data?.ship_address?.client_alternative_data && (
                    <>
                      <div className="row">
                        <div className="col-4 th-font-weight-500">
                          Alternative Contact Name
                        </div>
                        <div className="col-7">
                          <div className="d-flex">
                            <div className="pe-1">:</div>
                            <div>
                              {
                                data?.ship_address?.client_alternative_data
                                  ?.contact_person_name
                              }
                            </div>
                          </div>
                        </div>
                      </div>
                      <div className="row">
                        <div className="col-4 th-font-weight-500">
                          Alternative Contact Contact
                        </div>
                        <div className="col-7">
                          <div className="d-flex">
                            <div className="pe-1">:</div>
                            <div>
                              {
                                data?.ship_address?.client_alternative_data
                                  ?.contact_person_contact
                              }
                            </div>
                          </div>
                        </div>
                      </div>
                    </>
                  )}
                </div>
              </div>
            )}
          </div>
          <div>
            {!copy_clipboard_select && (
              <Button
                onClick={() => {
                  setCopyClipboardSelect(true);
                }}
                className="mb-3"
                type="primary"
              >
                Select items to copy
              </Button>
            )}

            <div className="th-font-size-16 th-font-weight-600 mb-2">
              {copy_clipboard_select ? <> Select Items to raise PO</> : <> </>}
            </div>
            {copy_clipboard_select ? (
              <div className="d-flex gap-2 py-3">
                <Button
                  disabled={!hasSelected}
                  onClick={() => {
                    let items = data?.estimate_items?.filter((item: any) => {
                      return selectedRowKeys?.includes(item?.id);
                    });
                    let copy_text = "Lead ID:" + data?.lead_id + "\n\n";
                    items?.map((item: any) => {
                      copy_text +=
                        item?.private_label_product_name +
                        " --" +
                        item?.quantity +
                        " " +
                        item?.unit +
                        ", Brand: " +
                        item?.brand +
                        "\n";
                    });
                    navigator.clipboard.writeText(copy_text);
                    message.success("Copied to clipboard");
                    setCopyClipboardSelect(false);
                  }}
                  type="primary"
                >
                  Copy to Clipboard
                </Button>
                <Button
                  type="default"
                  onClick={() => {
                    setCopyClipboardSelect(false);
                  }}
                >
                  Cancel
                </Button>
              </div>
            ) : (
              <></>
            )}
            {copy_clipboard_select ? (
              <Table
                rowSelection={rowSelection}
                columns={columns}
                dataSource={data?.estimate_items}
                scroll={{ x: "max-content" }}
                rowKey={(record) => record.id}
              />
            ) : (
              <Table
                columns={columns}
                dataSource={data?.estimate_items}
                scroll={{ x: "max-content" }}
                rowKey={(record) => record.id}
              />
            )}
          </div>
          <div className="row">
            <div className="col-xl-6 col-12">
              <div>
                <h6 className="th-font-weight-600">Payment Terms</h6>
                <div>
                  {data?.estimate_payment_terms?.map(
                    (each: any, ind: number) => (
                      <div key={ind}>
                        {each?.term}:{each?.percentage}%
                      </div>
                    )
                  )}
                </div>
              </div>
              <Divider />
              <div>
                <div>
                  <h6 className="th-font-weight-600">Terms and Conditions</h6>
                  <div>
                    <div className="mb-2">
                      {data?.terms_and_condition?.description}
                    </div>
                  </div>
                </div>
              </div>
              <Divider />

              {/* <div>
                <div className="">
                  <div>
                    <h6 className="th-font-weight-600">Documents</h6>{" "}
                    {"(Total files size should be less than 25MB)"}
                  </div>
                  {data?.documents?.map((document: any, index: any) => {
                    return (
                      <div style={{ padding: 8 }}>
                        <div
                          style={{
                            border: "1px #D1D0CE solid",
                            padding: 4,
                            cursor: "pointer",
                            fontWeight: 500,
                          }}
                          onClick={() => {
                            // openInNewTab(document?.file);
                          }}
                        >
                          {document?.file_name ??
                            document?.file?.replace(
                              "https://mysite-powo-pdfs.s3.ap-south-1.amazonaws.com/media/porject_files/",
                              ""
                            )}
                        </div>
                      </div>
                    );
                  })}
                  {data?.old_attachment !=== "" &&
                    JSON.parse(data?.old_attachment ?? "[]")?.map(
                      (document) => {
                        return (
                          <div style={{ padding: 8 }}>
                            <div
                              style={{
                                border: "1px #D1D0CE solid",
                                padding: 4,
                                cursor: "pointer",
                                fontWeight: 500,
                              }}
                              onClick={() => {
                                const extension = document.split(".").pop();
                                handlePdfDownload(
                                  document.replace(`.${extension}`, ""),
                                  extension
                                );
                                // openInNewTab(
                                //   "https://mysite-powo-pdfs.s3.ap-south-1.amazonaws.com/" +
                                //     document
                                // );
                              }}
                            >
                              {document ?? ""}
                            </div>
                          </div>
                        );
                      }
                    )}
                </div>
              </div> */}
            </div>
            <div className="col-xl-6 col-12">
              <div>
                <div className="d-flex justify-content-between">
                  {/* <div className="">Taxable Amount</div> */}
                  <div className="">
                    {/* ₹ {Number(getTotalTaxableAmount()).toFixed(2)} */}
                  </div>
                </div>
                <TaxAmount items={data?.estimate_items ?? []} />
              </div>
              <Divider />
              <div className="d-flex justify-content-end mb-2">
                <Tooltip
                  title={
                    !hasPermission("expressunloadingtag.addel")
                      ? "You don't have permission to add charges"
                      : CHARGES_LOCKED_STATUSES.includes(data?.estimate_status)
                      ? "Charges can't be changed once the order is placed"
                      : piEditLock ?? undefined
                  }
                >
                  <Button
                    type="primary"
                    icon={<PlusOutlined />}
                    disabled={
                      !hasPermission("expressunloadingtag.addel") ||
                      CHARGES_LOCKED_STATUSES.includes(data?.estimate_status) ||
                      Boolean(piEditLock)
                    }
                    onClick={() => setAddChargesDrawerVisible(true)}
                  >
                    Add Charges
                  </Button>
                </Tooltip>
              </div>
              <div>
                {data?.estimate_additional_charges?.map(
                  (each: any, ind: number) => (
                    <>
                      <div className="d-flex justify-content-between" key={ind}>
                        <div className="">
                          {each?.charge_type}
                          {each?.charge_type
                            ?.toLowerCase()
                            ?.includes("discount")
                            ? " (as per item)"
                            : ` (@${each?.tax_value + "%"} ${each?.tax_type})`}
                        </div>
                        <div className="d-flex align-items-center gap-2">
                          {Number(
                            Number(each?.amount) +
                              Number(each?.amount) *
                                Number(each?.tax_value) *
                                0.01
                          ).toFixed(2)}
                          {canRemoveCharge(each) && (
                            <Popconfirm
                              title="Remove this charge?"
                              description={
                                each?.remark?.toLowerCase() === "express delivery"
                                  ? "Standard delivery is added back if the total drops below ₹25,000."
                                  : undefined
                              }
                              onConfirm={() => onRemoveCharge(each?.id)}
                              okText="Remove"
                              okButtonProps={{ danger: true }}
                              cancelText="Cancel"
                            >
                              <Button
                                type="text"
                                size="small"
                                danger
                                icon={<DeleteOutlined />}
                                aria-label={`Remove ${each?.charge_type}`}
                              />
                            </Popconfirm>
                          )}
                        </div>
                      </div>
                      {each.remark && each.remark != "" && (
                        <div>Remark: {each.remark}</div>
                      )}
                    </>
                  )
                )}
                {parseFloat(data?.coupon_discount_amount ?? "0") > 0 && (
                  <>
                    <div className="d-flex justify-content-between">
                      <div className="">Coupon Discount</div>
                      <div className="">
                        ₹ {Number(data?.coupon_discount_amount).toFixed(2)}
                      </div>
                    </div>
                    <div className="d-flex justify-content-between">
                      <div className="th-font-size-12">Coupon Code</div>
                      <div className="th-font-size-12">
                        {data?.coupon_data?.coupon_code}
                      </div>
                    </div>
                  </>
                )}
              </div>
              <Divider />
              <div>
                <div className="d-flex justify-content-between">
                  <div className="">
                    <h6>Total Amount</h6>
                  </div>
                  <div className="">
                    ₹ {Number(data?.total_estimate_amount).toFixed(2)}
                  </div>
                </div>
                <div className="d-flex justify-content-between mt-2">
                  <div className="text-secondary th-font-size-14">Wallet Balance</div>
                  <div className="text-secondary th-font-size-14">
                    ₹ {Number(paymentSummary?.applicable_wallet ?? 0).toFixed(2)}
                  </div>
                </div>
                <div className="d-flex justify-content-between mt-2">
                  <div className="text-secondary th-font-size-14">Amount Paid</div>
                  <div className="text-secondary th-font-size-14">
                    ₹ {Number(paymentSummary?.paid_amount ?? 0).toFixed(2)}
                  </div>
                </div>
                <Divider style={{ margin: "12px 0" }} />
                <div className="d-flex justify-content-between">
                  <div className="">
                    <h6>Amount to be paid</h6>
                  </div>
                  <div className="">
                    <h6 className="mb-0">₹ {amountPending.toFixed(2)}</h6>
                  </div>
                </div>
                {amountPending === 0 && ["quote_approval_pending", "payment_pending", "draft", "incomplete"].includes(data?.estimate_status) && (
                  <div className="mt-4 text-end">
                    <Button type="primary" loading={applyingWallet} onClick={handleSettle} className="px-4">
                      Place Order
                    </Button>
                  </div>
                )}
              </div>
            </div>
          </div>
          <Divider />
          <div>
            <div className="th-font-size-18 th-font-weight-500">Invoices</div>
            <div className="d-flex gap-2 py-4 flex-wrap">
              {invoices?.map((invoice) => {
                return (
                  <div
                    onClick={() => {
                      window.open(invoice?.invoice_file, "_blank");
                    }}
                    className="p-3 px-4 border rounded-3 cursor-pointer"
                  >
                    <div className="th-font-size-16">
                      Invoice number : {invoice?.invoice_number}
                    </div>
                    <div className="th-font-size-16">
                      Invoice amount : {invoice?.invoice_value}
                    </div>
                    <div className="d-flex justify-content-between mt-3">
                      <Popconfirm
                        title="Delete the task"
                        description="Are you sure to delete this invoice?"
                        onConfirm={() => {
                          confirm(invoice?.id);
                        }}
                        onCancel={cancel}
                        okText="Yes"
                        cancelText="No"
                      >
                        <Button
                          className="d-flex align-items-center"
                          danger
                          loading={loading}
                        >
                          <DeleteOutlined />
                        </Button>
                      </Popconfirm>
                      <Button
                        className=""
                        loading={loading}
                        onClick={() => {
                          window.open(
                            String(
                              invoice?.invoice_file ??
                                invoice?.invoice_file_link
                            ).replaceAll(
                              "mysite-powo-pdfs",
                              "materialdepotimages"
                            ),
                            "_blank"
                          );
                        }}
                      >
                        Open
                      </Button>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>

      {transactions?.length > 0 && (
        <div className="m-4 p-4 px-5 white-color-bg">
          <div className="primary-color-text th-font-size-18 th-font-weight-700">
            Transactions Recorded
          </div>
          <div className="py-3">
            <Table
              dataSource={transactions}
              columns={transactionColumns}
              rowKey={(record) => record.id}
              scroll={{
                x: "100%",
              }}
            />
          </div>
        </div>
      )}

      <Drawer
        title="Basic Drawer"
        placement="right"
        onClose={() => {
          setSeeLogs(false);
        }}
        open={see_logs}
      >
        {logs?.map((log) => {
          return <ProFormaLogCard log={log} />;
        })}
      </Drawer>

      <DeliveryAddressDrawer
        data={data}
        visible={delivery_address_drawer_visible}
        onFinish={onDeliveryDetailsFinish}
        onClose={() => {
          setDeliveryAddressDrawerVisible(false);
        }}
      />
      <Drawer
        open={Boolean(showEditPIEditAddress)}
        onClose={() => {
          setShowEditPIEditAddress(null);
        }}
        title="Edit Address"
      >
        {showEditPIEditAddress && (
          <PIEditAddress
            estimate={data}
            type={showEditPIEditAddress}
            refresh={getPIData}
            onClose={() => setShowEditPIEditAddress(null)}
          />
        )}
      </Drawer>

      <AddChargesDrawer
        open={addChargesDrawerVisible}
        onClose={() => setAddChargesDrawerVisible(false)}
        onSuccess={() => {
          getPIData();
          refreshTransactions();
          refreshPaymentSummary();
        }}
        estimate={data}
      />
      <GSTRetrofitSidebar
        open={activitySidebarOpen}
        onClose={() => setActivitySidebarOpen(false)}
        estimateId={data?.id}
        leadId={data?.lead_id}
      />
    </section>
  );
};

export default ProFormaInvoiceDetails;
